import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell } from '../components';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n/I18nProvider';
import { ApiError, NetworkError } from '../api/client';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

/**
 * Accesso operatore.
 *
 * La root dell'applicazione è questa schermata, non una pagina marketing (§10).
 */
export function LoginPage() {
  const auth = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (auth.status === 'authenticated') return <Navigate to="/app" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await auth.login(email, password);
    } catch (cause) {
      // Il messaggio non distingue "utente inesistente" da "password errata":
      // rispecchia la risposta del server, che è volutamente indistinta.
      if (cause instanceof ApiError && cause.status === 401) {
        setError(t.login.failed);
      } else if (cause instanceof NetworkError) {
        setError(t.errors.offline);
      } else {
        setError(t.login.genericError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AcsPageShell
      centered
      header={
        <>
          <AcsLogo />
          <LanguageSwitcher />
        </>
      }
    >
      <AcsCard className="acs-stack" >
        <div style={{ maxWidth: '24rem' }}>
          <h1 style={{ fontSize: 'var(--acs-font-size-xl)', margin: 0 }}>{t.login.title}</h1>
          <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.login.subtitle}</p>

          <form onSubmit={handleSubmit} noValidate>
            <AcsInput
              label={t.login.email}
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <AcsInput
              label={t.login.password}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error ? (
              <p className="acs-field__error" role="alert">
                {error}
              </p>
            ) : null}

            <AcsButton type="submit" block disabled={submitting}>
              {submitting ? t.login.submitting : t.login.submit}
            </AcsButton>
          </form>
        </div>
      </AcsCard>
    </AcsPageShell>
  );
}
